"use client";

import { COLOR, Dot, MeasureArrow, IllustrationCard } from "./_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

// ── Diversification illustrations ───────────────────────────────────

const STRINGS: Record<Locale, Record<string, string>> = {
  ja: {
    timeTitle: "時間分散",
    timeDescription: "買うタイミングを分けて、高値づかみの偏りを減らす",
    assetTitle: "銘柄分散",
    assetDescription: "1社・1地域・1資産への集中を避ける",
    splitTiming: "タイミングを分ける",
    equity: "株式",
    bond: "債券",
    reit: "REIT",
    gold: "金",
  },
  en: {
    timeTitle: "Spread over time",
    timeDescription: "Buy at intervals so you are less exposed to a single high entry point",
    assetTitle: "Spread across holdings",
    assetDescription: "Avoid concentrating in one company, one region, or one asset",
    splitTiming: "Split the timing",
    equity: "Equities",
    bond: "Bonds",
    reit: "REITs",
    gold: "Gold",
  },
};

const VW = 240;
const VH = 120;

const BUY_POINTS = [
  { x: 36, y: 64 },
  { x: 80, y: 44 },
  { x: 124, y: 74 },
  { x: 168, y: 48 },
  { x: 212, y: 58 },
] as const;

function TimeDiversificationIllustration() {
  const t = STRINGS[useDocLocale()];
  const c = COLOR.sky;
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full">
      <line x1={20} y1={90} x2={220} y2={90} stroke="currentColor" strokeWidth="0.5" strokeOpacity={0.1} />
      <path
        d="M 20 68 Q 42 56, 60 46 Q 80 36, 100 46 Q 124 64, 140 74 Q 168 56, 184 48 Q 210 42, 224 54"
        fill="none"
        stroke={c}
        strokeWidth="2"
        strokeLinecap="round"
      />
      {BUY_POINTS.map((p) => (
        <g key={p.x}>
          <line x1={p.x} y1={p.y} x2={p.x} y2={90} stroke={c} strokeWidth="0.8" strokeDasharray="2 3" />
          <Dot x={p.x} y={p.y} color={c} />
        </g>
      ))}
      <MeasureArrow
        x1={BUY_POINTS[0].x}
        y1={104}
        x2={BUY_POINTS[BUY_POINTS.length - 1].x}
        y2={104}
        color={c}
        label={t.splitTiming}
      />
    </svg>
  );
}

const PIE_SEGMENTS = [
  { color: COLOR.sky, key: "equity" },
  { color: COLOR.amber, key: "bond" },
  { color: "#ec4899", key: "reit" },
  { color: COLOR.emerald, key: "gold" },
] as const;

function AssetDiversificationIllustration() {
  const t = STRINGS[useDocLocale()];
  const cx = 80;
  const cy = 52;
  const r = 36;
  const n = PIE_SEGMENTS.length;
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full">
      {PIE_SEGMENTS.map((seg, i) => {
        const a0 = (i / n) * Math.PI * 2 - Math.PI / 2;
        const a1 = ((i + 1) / n) * Math.PI * 2 - Math.PI / 2;
        const d = `M ${cx} ${cy} L ${cx + r * Math.cos(a0)} ${cy + r * Math.sin(a0)} A ${r} ${r} 0 0 1 ${cx + r * Math.cos(a1)} ${cy + r * Math.sin(a1)} Z`;
        return <path key={seg.key} d={d} fill={seg.color} fillOpacity={0.2} stroke={seg.color} strokeWidth="1.2" />;
      })}
      <circle
        cx={cx}
        cy={cy}
        r={15}
        className="fill-white dark:fill-zinc-900"
        stroke="currentColor"
        strokeWidth="0.5"
        strokeOpacity={0.2}
      />
      {PIE_SEGMENTS.map((seg, i) => (
        <g key={seg.key}>
          <circle
            cx={160}
            cy={20 + i * 22}
            r={4}
            fill={seg.color}
            fillOpacity={0.3}
            stroke={seg.color}
            strokeWidth="0.8"
          />
          <text x={170} y={24 + i * 22} fontSize="9" fill="currentColor">
            {t[seg.key]}
          </text>
        </g>
      ))}
    </svg>
  );
}

// ── Export ───────────────────────────────────────────────────────────

export function DocDiversificationFlowDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <IllustrationCard title={t.timeTitle} description={t.timeDescription}>
        <TimeDiversificationIllustration />
      </IllustrationCard>
      <IllustrationCard title={t.assetTitle} description={t.assetDescription}>
        <AssetDiversificationIllustration />
      </IllustrationCard>
    </div>
  );
}
